import styled, { css, keyframes } from "styled-components";

import { colors, fonts } from "../../../styles/variables";

/* How long the morph's crossfade runs. Surface.tsx keeps the panel mounted
   for exactly this long after close so the fade-out can play. */
export const MORPH_ANIMATION_MS = 250;

const morphFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const morphFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

/* The detail rows take the card image's place on the card's own surface
   colour — a replacement, not an overlay, so nothing needs to blend and the
   card and grid never change height. Claims the full card width (countering
   the body's 15px padding). Bleeding sideways is invisible since the panel
   matches the card body's colour.
   (Shared by ProductCardCellar and WineCardCellar via Surface.tsx.) */
export const MorphPanel = styled.div<{ $backgroundColor?: string; $closing?: boolean }>`
  position: absolute;
  top: 0;
  /* Ends level with the image area, which already reaches the footer: the
     card bodies are flex columns, so ImageArea is a flex item (an independent
     formatting context) and the image container's 25px bottom margin counts
     inside its height rather than collapsing through it. Overhanging by that
     25px as well pushed the panel into the footer's more-options row. */
  bottom: 0;
  left: -15px;
  right: -15px;
  /* Bottom padding sits the Done button the same ~14px clear of the
     more-options dots below as its own top margin gives it from the note
     above (the dots' ink is inset ~11px into the footer). */
  padding: 4px 15px;
  /* Bottom-anchor the content (via DetailsWrapper's margin-top: auto): any
     spare height collects at the TOP of the panel, which is exactly where
     the "N points earned!" toast floats — so on typical cards the toast
     lands on empty space rather than the first row's stars, without
     reserving fixed padding that overflows short cards into scrollbars. */
  display: flex;
  flex-direction: column;
  /* Safety valve for unusually short cards only. */
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${({ $backgroundColor }) => $backgroundColor || colors.silverSoft};
  animation: ${(props) => (props.$closing ? morphFadeOut : morphFadeIn)} ${MORPH_ANIMATION_MS}ms ease both;

  ${(props) =>
    props.$closing &&
    css`
      /* A fading panel is already dismissed — taps mid-animation should
         reach whatever is underneath, not its fading controls. */
      pointer-events: none;
    `}
`;

export const DetailsWrapper = styled.div`
  position: relative;
  width: 100%;
  /* Inside the card panel's flex column this pushes the content to the
     bottom, leaving the leftover height at the top as toast clearance (see
     MorphPanel). Inert in the review tray's normal flow (auto → 0). */
  margin-top: auto;
`;

/* Shared compact outlined-pill treatment for the boolean choices and the
   panel's final action. */
const outlinedPillButtonStyles = css`
  border-radius: 999px;
  /* Explicit line box + a 1px-heavier bottom pad: BauPro's em box carries a
     13px ascent but its caps ink is only ~9px tall, so glyphs land ~2px low
     when the box is padded evenly — measured via canvas TextMetrics, the
     6/7 split centres the ink for Yes / No / DONE to within 0.1px. */
  padding: 6px 14px 7px;
  line-height: 12px;
  font-family: ${fonts.fontBauRegular};
  font-size: 12px;
  cursor: pointer;
  border: 1px solid ${colors.gray};
  background: transparent;
  color: ${colors.black};
  transition: all 200ms ease;

  &:hover,
  &:focus {
    border-color: ${colors.black};
    outline: none;
  }
`;

/* Sits centred under the rows as the panel's final action — black-outlined
   version of the Yes / No pill, filling black on hover/press. */
export const DoneButton = styled.button.attrs({ type: "button" })`
  ${outlinedPillButtonStyles}
  display: flex;
  align-items: center;
  /* Same 14px breathing room above as the rows → textarea gap, so the
     button reads centred between the note and the panel's bottom edge. */
  margin: 14px auto 0;
  font-family: ${fonts.fontBauMedium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-color: ${colors.black};

  &:hover,
  &:focus,
  &:active {
    background: ${colors.black};
    color: ${colors.white};
    border-color: ${colors.black};
  }
`;

export const DetailRows = styled.div<{ $form?: boolean }>`
  /* Two columns shared by every row: labels centred in the left one,
     controls centred in the right one. The right column auto-sizes to its
     widest control (the star strip), so the yes/no pills centre within the
     same space the stars occupy. */
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  row-gap: 6px;
  padding: 4px 0 8px;

  ${(props) =>
    props.$form &&
    css`
      /* Review-form variant: the hierarchy is carried by breathing room
         rather than smaller type — generous gaps between the rows and
         around the block (which sits between the overall row and the
         note textarea). */
      row-gap: 15px;
      padding: 15px 0 20px;
    `}
`;

export const DetailRow = styled.div`
  /* The label and control place themselves directly into the shared grid
     columns; this wrapper only groups them semantically (and for tests). */
  display: contents;
`;

/* Card-panel heading, sitting at the top of the bottom-anchored content —
   visually below the points toast when it's showing. Same small-caps voice
   as the disclosure heading on this surface. */
export const PanelHeading = styled.p`
  font-family: ${fonts.fontBauMedium};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.black};
  text-align: center;
  margin: 0 0 4px;
`;

/* The app's small-label idiom (see WineExplanationFeedback's FeedbackPrompt).
   The form variant drops the small-caps treatment and matches the review
   form's own text instead — same size and colour as the copy beside the
   overall stars. */
export const DetailLabel = styled.span<{ $form?: boolean }>`
  font-family: ${fonts.fontBauMedium};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.gray};
  text-align: left;
  padding-right: 12px;

  ${(props) =>
    props.$form &&
    css`
      font-family: ${fonts.fontBauRegular};
      font-size: 14px;
      text-transform: none;
      letter-spacing: normal;
      color: ${colors.black};
      padding-right: 20px;
    `}
`;

export const DetailControl = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

/* Black fill when selected, gray outline when not. The pair grows to fill the
   control column (which the grid sizes to the star strip), so Yes / No span
   the same width the stars do — their outer edges land on the stars' margins. */
export const BooleanChoiceButton = styled.button.attrs({ type: "button" })<{
  $active: boolean;
  $disabled: boolean;
}>`
  ${outlinedPillButtonStyles}
  flex: 1;
  border-color: ${(props) => (props.$active ? colors.black : colors.gray)};
  background: ${(props) => (props.$active ? colors.black : "transparent")};
  color: ${(props) => (props.$active ? colors.white : colors.black)};

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}
`;

export const ReadOnlyValue = styled.span`
  font-family: ${fonts.fontBauRegular};
  font-size: 13px;
  color: ${colors.black};
`;

/* A compact take on the shared input signature (atoms/Input inputStyles):
   Concept font, 1px black border, square corners, faded placeholder. The
   full Textarea atom carries a 40px top margin and 60px height that don't
   fit the card panel. */
export const NoteTextarea = styled.textarea`
  /* Block display: as an inline-block the textarea leaves a phantom ~4px
     baseline strut below itself, which silently pads the gap to the Done
     button. */
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 10px 15px;
  border: 1px solid ${colors.black};
  border-radius: 0;
  background-color: ${colors.white};
  font-family: ${fonts.fontConceptRegular};
  font-size: 13px;
  color: ${colors.black};
  line-height: 1.3;
  resize: none;

  &::placeholder {
    color: ${colors.black};
    font-family: ${fonts.fontConceptRegular};
    opacity: 0.7;
  }

  &:focus {
    outline: none;
  }
`;
